import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  createShopifyCart,
  closeCheckoutWindow,
  navigateToCheckout,
  type CartLineInput,
  type CheckoutBuyerIdentity,
} from '@/lib/shopify';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { trackAddToCart, trackBeginCheckout, type EcomItem } from '@/lib/analytics';
import { announce } from '@/lib/announce';

/**
 * Resilient storage: writes the cart to localStorage, sessionStorage, AND a
 * long-lived cookie. On read, falls back through them in order, so the cart
 * survives even if one layer is cleared (privacy mode, storage purges, etc.).
 */
const COOKIE_NAME = 'bloom-cart-bk';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((r) => r.startsWith(name + '='));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

const resilientStorage: Storage = {
  get length() {
    try { return localStorage.length; } catch { return 0; }
  },
  key(i: number) {
    try { return localStorage.key(i); } catch { return null; }
  },
  getItem(key: string) {
    try {
      const v = localStorage.getItem(key);
      if (v) return v;
    } catch { /* ignore */ }
    try {
      const v = sessionStorage.getItem(key);
      if (v) return v;
    } catch { /* ignore */ }
    return readCookie(COOKIE_NAME);
  },
  setItem(key: string, value: string) {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
    try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
    // Cookies have ~4KB cap — only persist when small enough to fit.
    if (value.length < 3500) writeCookie(COOKIE_NAME, value);
  },
  removeItem(key: string) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.cookie = `${COOKIE_NAME}=;path=/;max-age=0;SameSite=Lax`;
    }
  },
  clear() {
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    if (typeof document !== 'undefined') {
      document.cookie = `${COOKIE_NAME}=;path=/;max-age=0;SameSite=Lax`;
    }
  },
};


const toEcomItem = (i: { variantId: string; title: string; variantTitle: string; price: number; currencyCode: string; quantity: number }): EcomItem => ({
  item_id: i.variantId,
  item_name: i.title,
  variant: i.variantTitle,
  price: i.price,
  currency: i.currencyCode,
  quantity: i.quantity,
});

async function loadBuyerIdentity(): Promise<CheckoutBuyerIdentity | undefined> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return undefined;
    const email = user.email || undefined;
    const { data } = await supabase
      .from('saved_addresses')
      .select('recipient,line1,line2,city,state,zip,country,phone,is_default')
      .eq('user_id', user.id)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) return email ? { email } : undefined;

    const [first, ...rest] = (data.recipient || '').trim().split(/\s+/);
    return {
      email,
      phone: data.phone || undefined,
      address: {
        firstName: first || undefined,
        lastName: rest.join(' ') || undefined,
        address1: data.line1,
        address2: data.line2 || undefined,
        city: data.city,
        province: data.state || undefined,
        country: data.country,
        zip: data.zip,
        phone: data.phone || undefined,
      },
    };
  } catch {
    return undefined;
  }
}

export interface CartItem {
  variantId: string;
  title: string;
  variantTitle: string;
  price: number;
  currencyCode: string;
  quantity: number;
  image?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  isCheckingOut: boolean;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  setOpen: (open: boolean) => void;
  checkout: () => Promise<void>;
  buyNow: (item: Omit<CartItem, 'quantity'>, quantity?: number) => Promise<void>;
  totalItems: () => number;
  totalPrice: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isCheckingOut: false,

      addItem: (item, quantity = 1) => {
        trackAddToCart(toEcomItem({ ...item, quantity }));
        announce(
          i18n.t('cart.addedAnnouncement', {
            defaultValue: '{{title}} added to your bag',
            title: item.title,
          }),
        );

        set((state) => {
          const existing = state.items.find((i) => i.variantId === item.variantId);
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.variantId === item.variantId
                  ? { ...i, quantity: i.quantity + quantity }
                  : i,
              ),
            };
          }

          return {
            items: [...state.items, { ...item, quantity }],
          };
        });
      },

      removeItem: (variantId) => {
        set((state) => ({
          items: state.items.filter((i) => i.variantId !== variantId),
        }));
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }

        set((state) => ({
          items: state.items.map((i) =>
            i.variantId === variantId ? { ...i, quantity } : i,
          ),
        }));
      },

      clearCart: () => set({ items: [] }),
      toggleCart: () => set((s) => ({ isOpen: !s.isOpen })),
      setOpen: (open) => set({ isOpen: open }),

      checkout: async () => {
        const { items } = get();
        if (items.length === 0) return;

        const totalValue = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const currency = items[0]?.currencyCode || 'USD';
        trackBeginCheckout(items.map(toEcomItem), totalValue, currency);

        set({ isCheckingOut: true });
        try {
          const lines: CartLineInput[] = items.map((i) => ({
            merchandiseId: i.variantId,
            quantity: i.quantity,
          }));
          const buyer = await loadBuyerIdentity();
          const checkoutUrl = await createShopifyCart(lines, buyer);
          if (checkoutUrl) {
            navigateToCheckout(checkoutUrl);
            set({ isOpen: false });
          } else {
            toast.error('Could not open secure checkout. Please try again.');
          }
        } catch {
          toast.error('Checkout failed. Please try again.');
        } finally {
          set({ isCheckingOut: false });
        }
      },

      buyNow: async (item, quantity = 1) => {
        trackBeginCheckout([toEcomItem({ ...item, quantity })], item.price * quantity, item.currencyCode);
        set({ isCheckingOut: true });
        try {
          const buyer = await loadBuyerIdentity();
          const checkoutUrl = await createShopifyCart(
            [{ merchandiseId: item.variantId, quantity }],
            buyer,
          );
          if (checkoutUrl) {
            navigateToCheckout(checkoutUrl);
          } else {
            toast.error('Could not open secure checkout. Please try again.');
          }
        } catch {
          toast.error('Checkout failed. Please try again.');
        } finally {
          set({ isCheckingOut: false });
        }
      },

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      totalPrice: () => get().items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    }),
    {
      name: 'bloom-cart',
      storage: createJSONStorage(() => resilientStorage),
      partialize: (state) => ({
        items: state.items,
      }),
    },

  ),
);
